import { db } from '../../config/firebase'
import { doc, onSnapshot, getDoc } from 'firebase/firestore'

class ConfigService {
  constructor() {
    this.config = {
      free: { devices: 1, screens: 2 },
      trial: { devices: 1, screens: 2 }
    }
    this.listeners = new Set()
    this.unsubscribers = new Map()
    this.isLoading = false
    this.lastCacheUpdate = null
    this.cacheExpiryMs = 5 * 60 * 1000 // 5 minutes cache expiry
  }

  /**
   * Get configuration for a specific plan type
   * @param {string} planType - 'free' or 'trial'
   * @returns {Object} Configuration object with devices and screens limits
   */
  async getConfig(planType) {
    try {
      // Check if we have valid cached data
      if (this.isCacheValid() && this.config[planType]) {
        console.log(`📋 ConfigService: Serving ${planType} config from cache`)
        return this.config[planType]
      }

      console.log(`🌐 ConfigService: Fetching ${planType} config from Firebase...`)
      const configDoc = await getDoc(doc(db, 'config', planType))
      if (configDoc.exists()) {
        const configData = configDoc.data()
        // Update local cache
        this.config[planType] = configData
        console.log(`✅ ConfigService: ${planType} config fetched and cached`)
        return configData
      }
      // Return default values if config doesn't exist
      console.log(`⚠️ ConfigService: ${planType} config not found in Firebase, using defaults`)
      return this.config[planType] || { devices: 1, screens: 1 }
    } catch (error) {
      console.error(`❌ Error fetching ${planType} config:`, error)
      // Return default values on error
      return this.config[planType] || { devices: 1, screens: 1 }
    }
  }

  /**
   * Get all configuration
   * @returns {Object} All configuration data
   */
  async getAllConfig() {
    try {
      // Check if we have valid cached data
      if (this.isCacheValid() && this.isConfigLoaded()) {
        console.log('📋 ConfigService: Serving all config from cache')
        return this.config
      }

      console.log('🌐 ConfigService: Fetching all config from Firebase...')
      const [freeConfig, trialConfig] = await Promise.all([
        this.getConfig('free'),
        this.getConfig('trial')
      ])
      
      // Update local cache with fetched data
      this.config.free = freeConfig
      this.config.trial = trialConfig
      
      // Update cache timestamp
      this.lastCacheUpdate = Date.now()
      
      console.log('✅ ConfigService: All config fetched and cached')
      return {
        free: freeConfig,
        trial: trialConfig
      }
    } catch (error) {
      console.error('❌ Error fetching all config:', error)
      return this.config
    }
  }

  /**
   * Subscribe to configuration changes
   * @param {Function} callback - Callback function to execute when config changes
   * @param {string} planType - Optional: specific plan type to listen to ('free' or 'trial')
   * @returns {Function} Unsubscribe function
   */
  subscribeToConfig(callback, planType = null) {
    const listenerId = Math.random().toString(36).substr(2, 9)
    
    if (planType) {
      // Subscribe to specific plan type
      const unsubscribe = onSnapshot(
        doc(db, 'config', planType),
        (doc) => {
          if (doc.exists()) {
            const config = doc.data()
            callback({ [planType]: config })
          }
        },
        (error) => {
          console.error(`Error listening to ${planType} config:`, error)
        }
      )
      
      this.unsubscribers.set(listenerId, unsubscribe)
    } else {
      // Subscribe to all config changes
      const unsubscribeFree = onSnapshot(
        doc(db, 'config', 'free'),
        (doc) => {
          if (doc.exists()) {
            const freeConfig = doc.data()
            this.config.free = freeConfig
            callback(this.config)
          }
        },
        (error) => {
          console.error('Error listening to free config:', error)
        }
      )

      const unsubscribeTrial = onSnapshot(
        doc(db, 'config', 'trial'),
        (doc) => {
          if (doc.exists()) {
            const trialConfig = doc.data()
            this.config.trial = trialConfig
            callback(this.config)
          }
        },
        (error) => {
          console.error('Error listening to trial config:', error)
        }
      )

      this.unsubscribers.set(listenerId, () => {
        unsubscribeFree()
        unsubscribeTrial()
      })
    }

    this.listeners.add(listenerId)
    
    // Return unsubscribe function
    return () => {
      const unsubscribe = this.unsubscribers.get(listenerId)
      if (unsubscribe) {
        unsubscribe()
        this.unsubscribers.delete(listenerId)
      }
      this.listeners.delete(listenerId)
    }
  }

  /**
   * Get device limit for a specific plan
   * @param {string} planType - 'free' or 'trial'
   * @returns {number} Device limit
   */
  async getDeviceLimit(planType) {
    const config = await this.getConfig(planType)
    return config.devices || 1
  }

  /**
   * Get screen limit for a specific plan
   * @param {string} planType - 'free' or 'trial'
   * @returns {number} Screen limit
   */
  async getScreenLimit(planType) {
    const config = await this.getConfig(planType)
    return config.screens || 1
  }

  /**
   * Get configuration value with fallback
   * @param {string} planType - 'free' or 'trial'
   * @param {string} key - 'devices' or 'screens'
   * @param {*} fallback - Fallback value if config is not available
   * @returns {*} Configuration value or fallback
   */
  getConfigValue(planType, key, fallback = null) {
    if (this.config[planType] && this.config[planType][key] !== undefined) {
      return this.config[planType][key]
    }
    return fallback
  }

  /**
   * Get configuration value with automatic cache refresh if needed
   * @param {string} planType - 'free' or 'trial'
   * @param {string} key - 'devices' or 'screens'
   * @param {*} fallback - Fallback value if config is not available
   * @returns {Promise<*>} Configuration value or fallback
   */
  async getConfigValueAsync(planType, key, fallback = null) {
    if (this.shouldRefreshCache()) {
      await this.preloadConfig()
    }
    return this.getConfigValue(planType, key, fallback)
  }

  /**
   * Get current configuration (cached)
   * @returns {Object} Current configuration
   */
  getCurrentConfig() {
    return this.config
  }

  /**
   * Get current configuration, ensuring it's loaded first
   * @returns {Promise<Object>} Current configuration
   */
  async getCurrentConfigAsync() {
    if (this.shouldRefreshCache()) {
      await this.preloadConfig()
    }
    return this.config
  }

  /**
   * Check if configuration has been loaded from Firebase
   * @returns {boolean} True if config has been loaded, false otherwise
   */
  isConfigLoaded() {
    // Check if we have non-default values (indicating Firebase data was loaded)
    // Defaults are: free (1,1), trial (5,5) - so check if we have different values
    const hasFirebaseData = this.lastCacheUpdate !== null && this.isCacheValid()
    return hasFirebaseData
  }

  /**
   * Check if cache is valid (not expired)
   * @returns {boolean} True if cache is still valid, false if expired
   */
  isCacheValid() {
    if (!this.lastCacheUpdate) return false
    return (Date.now() - this.lastCacheUpdate) < this.cacheExpiryMs
  }

  /**
   * Check if cache needs refreshing
   * @returns {boolean} True if cache should be refreshed
   */
  shouldRefreshCache() {
    return !this.isCacheValid() || !this.isConfigLoaded()
  }

  /**
   * Get loading state
   * @returns {boolean} True if configuration is currently loading
   */
  getLoadingState() {
    return this.isLoading
  }

  /**
   * Preload all configuration data when app starts
   * This ensures configuration is available before other services need it
   */
  async preloadConfig() {
    try {
      this.isLoading = true
      console.log('🔄 Preloading ConfigService configuration...')
      await this.getAllConfig()
      console.log('✅ ConfigService configuration preloaded successfully')
    } catch (error) {
      console.error('❌ Error preloading ConfigService configuration:', error)
      // Don't throw - fall back to default config
    } finally {
      this.isLoading = false
    }
  }

  /**
   * Force refresh configuration from Firebase
   * Useful for admin updates or when config changes
   */
  async refreshConfig() {
    try {
      this.isLoading = true
      console.log('🔄 Refreshing ConfigService configuration...')
      await this.getAllConfig()
      console.log('✅ ConfigService configuration refreshed successfully')
    } catch (error) {
      console.error('❌ Error refreshing ConfigService configuration:', error)
      throw error
    } finally {
      this.isLoading = false
    }
  }

  /**
   * Invalidate cache to force refresh on next access
   */
  invalidateCache() {
    this.lastCacheUpdate = null
    console.log('🗑️ ConfigService cache invalidated')
  }

  /**
   * Get cache status information
   * @returns {Object} Cache status details
   */
  getCacheStatus() {
    return {
      isLoaded: this.isConfigLoaded(),
      isValid: this.isCacheValid(),
      shouldRefresh: this.shouldRefreshCache(),
      lastUpdate: this.lastCacheUpdate,
      cacheAge: this.lastCacheUpdate ? Date.now() - this.lastCacheUpdate : null,
      expiryMs: this.cacheExpiryMs,
      isLoading: this.isLoading,
      config: this.config
    }
  }

  /**
   * Cleanup all listeners
   */
  cleanup() {
    this.unsubscribers.forEach(unsubscribe => unsubscribe())
    this.unsubscribers.clear()
    this.listeners.clear()
  }
}

// Create singleton instance
const configService = new ConfigService()

export default configService
